import { useState } from 'react';
import { pipelineData } from '../../../mocks/dashboard';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

export default function LeadPipeline() {
  const [pipeline, setPipeline] = useState(pipelineData);
  const [dragging, setDragging] = useState<{ colIdx: number; leadIdx: number } | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const handleDragStart = (colIdx: number, leadIdx: number) => {
    setDragging({ colIdx, leadIdx });
  };

  const handleDrop = (targetColIdx: number) => {
    if (!dragging || dragging.colIdx === targetColIdx) {
      setDragging(null);
      setDragOver(null);
      return;
    }
    const updated = pipeline.map((col) => ({ ...col, leads: [...col.leads] }));
    const [movedLead] = updated[dragging.colIdx].leads.splice(dragging.leadIdx, 1);
    updated[targetColIdx].leads.push(movedLead);
    setPipeline(updated);
    setDragging(null);
    setDragOver(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Lead Pipeline</h2>
          <p className="text-xs text-gray-400 mt-0.5">Drag cards to update status</p>
        </div>
        <a href="/crm/leads" className="text-xs font-medium cursor-pointer hover:underline" style={{ color: brand }}>
          View All Leads →
        </a>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {pipeline.map((col, colIdx) => (
          <div
            key={col.status}
            className={`flex-shrink-0 w-56 rounded-xl p-3 transition-all ${
              dragOver === colIdx ? 'ring-2' : ''
            }`}
            style={{
              backgroundColor: col.bgColor,
              ...(dragOver === colIdx ? { ringColor: col.color } : {}),
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(colIdx); }}
            onDrop={() => handleDrop(colIdx)}
            onDragLeave={() => setDragOver(null)}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: col.color }}>
                {col.status}
              </span>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: col.color }}
              >
                {col.leads.length}
              </span>
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {col.leads.map((lead, leadIdx) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => handleDragStart(colIdx, leadIdx)}
                  className="bg-white rounded-xl p-3 cursor-grab active:cursor-grabbing hover:border-gray-200 border border-gray-100 transition-all"
                >
                  <div className="flex items-start justify-between gap-1 mb-1.5">
                    <p className="text-sm font-semibold text-gray-800 leading-snug">{lead.name}</p>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${lead.source} text-sm text-gray-400`} />
                    </div>
                  </div>
                  <span
                    className="inline-block text-xs px-2 py-0.5 rounded-full font-medium mb-2"
                    style={{ backgroundColor: `${col.color}15`, color: col.color }}
                  >
                    {lead.service}
                  </span>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-700">{lead.budget}</p>
                    <p className="text-xs text-gray-400">{lead.days} ago</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="mt-2 w-full text-xs font-medium py-2 rounded-xl border border-dashed border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 cursor-pointer transition-colors flex items-center justify-center gap-1"
            >
              <i className="ri-add-line" /> Add Lead
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
